
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from '@/components/ui/button';
import { Printer, RefreshCw, AlertCircle } from 'lucide-react';
import { useAdminActions } from '@/hooks/useAdminActions';
import type { AdminAction as AdminActionFromHook } from '@/hooks/useAdminActions';
import { printAdminActionHistoryAsJPG, printAdminActionHistoryAsPDF } from '@/utils/printing/historyPrinting';
import type { AdminAction as AdminActionForPrinting } from '@/services/db/admin/adminActionTracking';
import AdminFormatDialog from '@/components/admin/AdminFormatDialog';
import { toast } from 'sonner';
import AdminActionHistory from '@/components/admin/AdminActionHistory';
import { communicationService } from '@/services/communication/communicationService';

const AdminHistoryTab = () => {
  const [selectedActionType, setSelectedActionType] = useState<string>("all");
  const [adminActions, setAdminActions] = useState<AdminActionFromHook[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const { actions, refreshActions } = useAdminActions('system_settings');
  
  useEffect(() => {
    // Initial load of actions
    try {
      refreshActions();
      setHasError(false);
    } catch (error) {
      console.error("Error loading admin history:", error);
      setHasError(true);
      toast.error("Failed to load admin history");
    }
    
    // Set up periodic refresh
    const refreshInterval = setInterval(() => {
      try {
        refreshActions();
        setHasError(false);
      } catch (error) {
        console.error("Error refreshing admin history:", error);
      }
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, [refreshActions]);
  
  useEffect(() => {
    // Filter actions based on selected type
    if (selectedActionType && selectedActionType !== 'all') {
      setAdminActions(actions.filter(action => action.actionType === selectedActionType));
    } else {
      setAdminActions(actions);
    }
  }, [selectedActionType, actions]);

  // Log this view for tracking purposes
  useEffect(() => {
    try {
      communicationService.logCommunication({
        recipientId: 'system',
        type: 'notification',
        title: 'Admin History Access',
        message: 'Administrator accessed history management page',
        status: 'delivered',
        recipientInfo: {
          name: 'System'
        },
        content: {
          message: 'Administrator accessed history management page'
        }
      });
    } catch (error) {
      console.error("Error logging communication:", error);
    }
  }, []);
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshActions();
      toast.success("Admin history refreshed");
      setHasError(false);
    } catch (error) {
      console.error("Error refreshing admin history:", error);
      toast.error("Failed to refresh history");
      setHasError(true);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Convert AdminActionFromHook to AdminActionForPrinting
  const convertActionForPrinting = (action: AdminActionFromHook): AdminActionForPrinting => {
    // Map the action types to the expected format for printing
    let mappedActionType: AdminActionForPrinting['actionType'];
    
    switch (action.actionType) {
      case 'create':
      case 'update':
      case 'delete':
      case 'view':
        mappedActionType = 'student_record';
        break;
      case 'upload':
      case 'download':
      case 'approve':
      case 'reject':
        mappedActionType = 'document';
        break;
      default:
        mappedActionType = 'system_setting';
    }
    
    return {
      id: action.id,
      timestamp: action.timestamp,
      actionType: mappedActionType,
      description: action.description,
      performedBy: action.performedBy,
      details: action.details || {}
    };
  };
  
  const handlePrintHistory = async (format: 'jpg' | 'pdf') => {
    try {
      console.log(`🖨️ Starting ${format.toUpperCase()} download for admin history...`);
      console.log('📊 Admin actions to print:', adminActions);
      
      // Ensure we have actual data to print
      if (adminActions.length === 0) {
        // If no filtered actions, use all actions or create sample data
        const actionsToUse = actions.length > 0 ? actions : [
          {
            id: '1',
            entityId: 'system',
            entityType: 'system' as const,
            actionType: 'update' as const,
            performedBy: 'System Administrator',
            timestamp: new Date().toISOString(),
            description: 'System accessed - Admin history viewed',
            details: { action: 'History management access' },
            metadata: {}
          }
        ];
        
        const actionsForPrinting = actionsToUse.map(convertActionForPrinting);
        
        if (format === 'jpg') {
          await printAdminActionHistoryAsJPG(actionsForPrinting, selectedActionType);
        } else {
          await printAdminActionHistoryAsPDF(actionsForPrinting, selectedActionType);
        }
      } else {
        // Convert AdminAction[] to the format expected by print functions
        const actionsForPrinting = adminActions.map(convertActionForPrinting);
        console.log('📋 Converted actions for printing:', actionsForPrinting);
        
        if (format === 'jpg') {
          await printAdminActionHistoryAsJPG(actionsForPrinting, selectedActionType);
        } else {
          await printAdminActionHistoryAsPDF(actionsForPrinting, selectedActionType);
        }
      }
      
      console.log(`✅ ${format.toUpperCase()} download completed successfully`);
    } catch (error) {
      console.error(`❌ Error downloading admin history ${format.toUpperCase()}:`, error);
      toast.error(`Failed to download admin history as ${format.toUpperCase()}`);
    }
  };

  const renderContent = () => {
    if (hasError) {
      return (
        <div className="p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Failed to load admin history</h3>
          <p className="text-muted-foreground mb-4">There was an error loading the history data.</p>
          <Button onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Try Again
          </Button>
        </div>
      );
    }

    if (adminActions.length === 0) {
      return (
        <div className="p-8 text-center">
          <p className="text-muted-foreground mb-4">No history records found for the selected filter.</p>
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      );
    }

    return (
      <AdminActionHistory 
        actions={adminActions.map(convertActionForPrinting)} 
        title={`${selectedActionType === 'all' ? 'All' : selectedActionType.replace('_', ' ')} Actions`} 
      />
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Administrative History Records</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Select value={selectedActionType} onValueChange={setSelectedActionType}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Select action type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="student_record">Student Records</SelectItem>
                <SelectItem value="document">Documents</SelectItem>
                <SelectItem value="permission">Permissions</SelectItem>
                <SelectItem value="system_setting">System Settings</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2 ml-auto">
              <Button 
                variant="outline" 
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowFormatDialog(true)}
              >
                <Printer className="h-4 w-4 mr-2" />
                Download History
              </Button>
            </div>
          </div>

          {renderContent()}
        </div>

        <AdminFormatDialog
          isOpen={showFormatDialog}
          onClose={() => setShowFormatDialog(false)}
          onDownload={handlePrintHistory}
          downloadType="list"
        />
      </CardContent>
    </Card>
  );
};

export default AdminHistoryTab;
